import React, { useRef, useEffect } from 'react';

const DIGIT_COUNT = 6;

function digitsFromValue(value) {
    const clean = String(value || '').replace(/\D/g, '').slice(0, DIGIT_COUNT);
    const arr = clean.split('');
    while (arr.length < DIGIT_COUNT) {
        arr.push('');
    }
    return arr;
}

export default function SegmentedOtpInput({ value, onChange, disabled, autoFocus }) {
    const inputsRef = useRef([]);
    const digits = digitsFromValue(value);

    useEffect(() => {
        if (autoFocus && !disabled) {
            inputsRef.current[0]?.focus();
        }
    }, [autoFocus, disabled]);

    function emit(nextDigits) {
        onChange(nextDigits.join('').slice(0, DIGIT_COUNT));
    }

    function focusIndex(index) {
        const el = inputsRef.current[index];
        if (el) {
            el.focus();
            el.select();
        }
    }

    function handleChange(index, raw) {
        const digit = raw.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[index] = digit;
        emit(next);
        if (digit && index < DIGIT_COUNT - 1) {
            focusIndex(index + 1);
        }
    }

    function handleKeyDown(index, event) {
        if (event.key === 'Backspace') {
            if (digits[index]) {
                const next = [...digits];
                next[index] = '';
                emit(next);
            } else if (index > 0) {
                event.preventDefault();
                const next = [...digits];
                next[index - 1] = '';
                emit(next);
                focusIndex(index - 1);
            }
            return;
        }
        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            focusIndex(index - 1);
            return;
        }
        if (event.key === 'ArrowRight' && index < DIGIT_COUNT - 1) {
            event.preventDefault();
            focusIndex(index + 1);
        }
    }

    function handlePaste(event) {
        event.preventDefault();
        const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGIT_COUNT);
        if (!pasted) return;
        onChange(pasted);
        const focusAt = Math.min(pasted.length, DIGIT_COUNT - 1);
        focusIndex(focusAt);
    }

    return (
        <div className="advertiser-signup-otp-segmented" role="group" aria-label="6-digit verification code">
            {digits.map((digit, index) => (
                <input
                    key={index}
                    ref={(el) => {
                        inputsRef.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`Digit ${index + 1} of ${DIGIT_COUNT}`}
                    className={`advertiser-signup-otp-cell ${digit ? 'has-value' : ''}`}
                    value={digit}
                    disabled={disabled}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onFocus={(e) => e.target.select()}
                />
            ))}
        </div>
    );
}
